/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
import React from 'react';
import { styled, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import type { CustomThemeButtonProps } from '@atlaskit/button/custom-theme-button';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { componentWithFG } from '@confluence/feature-gate-component';

import { AUTO_DISMISS_RESOLVE_NOTIFICATION_ANIMATION_SPEED } from './inlineCommentConstants.ts';
import type { InlineCommentsMode } from './inlineCommentsTypes.ts';

type SidebarContainerProps = {
	offset: number;
	rightOffset?: number;
	mode?: InlineCommentsMode;
	isAutoDismissResolve?: boolean;
	isSticky?: boolean;
};

type NewReplyContainerProps = {
	mode: InlineCommentsMode;
	isCommentsPanel?: boolean;
	isFabricPage?: boolean;
	isStandaloneComment?: boolean;
};

type CommentNavigationProps = {
	shouldHaveButtonGap?: boolean;
};

const slideOut = keyframes({
	from: { transform: 'translateX(0)' },
	to: { transform: 'translateX(280px)' },
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionsContainer = styled.ul({
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	font: token('font.body.small'),
	padding: `${token('space.100')} 0 0 0`,
	minHeight: 28,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionText = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover span': {
		textDecoration: 'underline',
		cursor: 'pointer',
	},
});

// Editor feedback button has a z-index of 460; Table sticky header has a z-index of 500; Renderer Page Toolbar has a z-index of 12
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
export const SidebarContainer = styled.div<SidebarContainerProps>(`
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    z-index: ${(props: SidebarContainerProps) => (props.mode === 'view' ? 11 : 501)};
    border: ${token('border.width')} solid ${token('color.border')};
    border-radius: 3px;
    box-shadow: ${token('elevation.shadow.overflow')};
    background-color: ${token('elevation.surface')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    right: ${(props: SidebarContainerProps) => {
			const { rightOffset = 0 } = props;
			return `${rightOffset}px`;
		}};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    position: ${(props: SidebarContainerProps) => (props.isSticky ? 'fixed' : 'absolute')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    top: ${(props: SidebarContainerProps) => props.offset}px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    margin-bottom: 100px;
    width: 280px;
	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values, confluence-feature-gating/no-preconditioning -- Ignored via go/DSP-18766 */ ''}
	max-height: none;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    ${(props: SidebarContainerProps) =>
			Boolean(props.isAutoDismissResolve) &&
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			`animation: ${slideOut} ${AUTO_DISMISS_RESOLVE_NOTIFICATION_ANIMATION_SPEED}ms ease-in-out;`}
  `);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const SidebarContainerRounded = styled.div<SidebarContainerProps>(`
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    z-index: ${(props: SidebarContainerProps) => (props.mode === 'view' ? 11 : 501)};
    border: ${token('border.width')} solid ${token('color.border')};
    border-radius: ${token('radius.xlarge')};
    box-shadow: ${token('elevation.shadow.overlay')};
    background-color: ${token('elevation.surface')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    right: ${(props: SidebarContainerProps) => {
			const { rightOffset = 0 } = props;
			return `${rightOffset}px`;
		}};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    position: ${(props: SidebarContainerProps) => (props.isSticky ? 'fixed' : 'absolute')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    top: ${(props: SidebarContainerProps) => props.offset}px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    margin-bottom: 100px;
    width: 280px;
	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values, confluence-feature-gating/no-preconditioning -- Ignored via go/DSP-18766 */ ''}
	max-height: none;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    ${(props: SidebarContainerProps) =>
			Boolean(props.isAutoDismissResolve) &&
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			`animation: ${slideOut} ${AUTO_DISMISS_RESOLVE_NOTIFICATION_ANIMATION_SPEED}ms ease-in-out;`}
  `);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SidebarContainerWithFG = componentWithFG(
	'confluence_frontend_comment_sidebar_radius',
	SidebarContainerRounded,
	SidebarContainer,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ReplyListContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: `${token('elevation.surface')}`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div[data-comment-id]': {
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NewReplyContainer = styled.div<NewReplyContainerProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-unsafe-values, confluence-feature-gating/no-module-level-eval -- Ignored via go/DSP-18766
	borderTop: (props: NewReplyContainerProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		props.isCommentsPanel || (props.isFabricPage && fg('confluence_frontend_content_wrapper'))
			? 'none'
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				`1px solid ${token('color.border')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: (props: NewReplyContainerProps) => {
		// Early returns will treat expValEquals('confluence_comments_tuneup', 'isEnabled', true) as === true
		// regardless of its actual value. Using a let gets around this.
		let result;

		if (
			props.isStandaloneComment &&
			expValEquals('confluence_comments_tuneup', 'isEnabled', true)
		) {
			result = token('space.150');
		} else {
			const basePadding =
				props.mode === 'view'
					? `${token('space.150')} ${token('space.200')}`
					: `${token('space.100')} ${token('space.150')}`;
			result = props.isCommentsPanel ? '0' : basePadding;
		}

		return result;
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, confluence-feature-gating/no-preconditioning
	paddingBottom: (props: NewReplyContainerProps) => {
		// Early returns will treat expValEquals('confluence_comments_tuneup', 'isEnabled', true) as === true
		// regardless of its actual value. Using a let gets around this.
		let result;

		if (
			props.isStandaloneComment &&
			expValEquals('confluence_comments_tuneup', 'isEnabled', true)
		) {
			result = token('space.150');
		} else if (!props.isCommentsPanel && fg('confluence_frontend_content_wrapper')) {
			result = token('space.200');
		} else {
			result = fg('confluence_frontend_content_wrapper') ? token('space.150') : token('space.100');
		}

		return result;
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, confluence-feature-gating/no-module-level-eval -- Ignored via go/DSP-18766
	'input[placeholder="Reply"]': {
		height: 32,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, confluence-feature-gating/no-module-level-eval
		padding: fg('confluence_frontend_content_wrapper')
			? `${token('space.075')} ${token('space.100')}`
			: token('space.050'),
	},

	// Placeholder styles for Reply input
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, confluence-feature-gating/no-module-level-eval -- Ignored via go/DSP-18766
	'input[placeholder="Reply"]::placeholder': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, confluence-feature-gating/no-module-level-eval -- Ignored via go/DSP-18766
		color: fg('confluence_frontend_content_wrapper')
			? token('color.text.subtlest')
			: token('color.text.disabled'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, confluence-feature-gating/no-module-level-eval -- Ignored via go/DSP-18766
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ResolvedNotificationContainer = styled.div(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  display: flex;
  padding: ${token('space.400')} 0;
  align-items: center;
  flex-direction: column;
  & p {
    height: 36px;
    width: 205px;
    color: ${token('color.text')};
	font: ${token('font.body.small')};
    text-align: center;
  }
  & a {
    margin-top: ${token('space.150')};
    cursor: pointer;
    font: ${token('font.body.small')};
    text-align: center;
  }
`,
);

// View mode styled components
type EditorContainerProps = {
	isReply?: boolean;
	mode: InlineCommentsMode;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EditorContainer = styled.div<EditorContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  ${(props: EditorContainerProps) =>
		!props.isReply &&
		`
        padding: ${token('space.150')};
        border-bottom: ${token('border.width')} solid ${token('color.border')};
      `}
`,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NavigationContainer = styled.div<CommentNavigationProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  display: flex;
  > span:last-child {
    padding-left: 10px;
    vertical-align: text-top;
    color: ${token('color.text.subtle')};
	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
	${(props: CommentNavigationProps) => props.shouldHaveButtonGap && `gap: ${token('space.025')};`}
  }
`,
);

interface InlineHeaderButtonProps extends CustomThemeButtonProps {
	isSSRFocusedComment?: boolean;
}

interface InlineCloseButtonProps extends CustomThemeButtonProps {
	isUpdatedStandaloneComment?: boolean;
}

export const HEADER_BUTTON_HEIGHT = '24px';
export const HEADER_BUTTON_WIDTH = '28px';
export const HEADER_BUTTON_WIDTH_STANDALONE = '24px'; // Needed to get the correct overall size to match the comment icon next to it

const inlineHeaderButtonTheme =
	(isSSRFocusedComment?: boolean) => (currentTheme: any, themeProps: any) => {
		const theme = currentTheme(themeProps);

		return {
			...theme,
			buttonStyles: {
				...theme.buttonStyles,
				background: 'none',
				borderRadius: token('radius.small'),
				height: HEADER_BUTTON_HEIGHT,
				width: HEADER_BUTTON_HEIGHT,
				'&:hover': {
					backgroundColor: `${token('elevation.surface.hovered')}`,
				},
				'&:focus': {
					boxShadow: `0 0 0 2px ${token('color.border.focused')}`,
				},
				cursor: isSSRFocusedComment ? 'not-allowed' : 'pointer',
				'*': {
					color: isSSRFocusedComment ? `${token('color.text.subtlest')}` : `inherit`,
				},
			},
		};
	};

export const InlineHeaderButton = ({ isSSRFocusedComment, ...props }: InlineHeaderButtonProps) => (
	<CustomThemeButton theme={inlineHeaderButtonTheme(isSSRFocusedComment)} {...props} />
);

type SidebarHeaderProps = {
	shouldShowNavigation?: boolean;
	isReattachHeader?: boolean;
};

type LoadingSkeletonContainerProps = {
	showSkeleton: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SidebarHeader = styled.div<SidebarHeaderProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  position: relative;
  display: flex;
  padding-left: ${token('space.150')};
  padding-right: ${token('space.150')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  justify-content: ${(props: SidebarHeaderProps) =>
		props.shouldShowNavigation ? 'space-between' : 'flex-end'};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  ${(props: SidebarHeaderProps) =>
		props.shouldShowNavigation && `height: 25px; padding-top: ${token('space.150')};`}
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  ${(props: SidebarHeaderProps) =>
		props.isReattachHeader && `height: 25px; padding-top: ${token('space.200')};`}
`,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LoadingSkeletonContainer = styled(SidebarContainer)<LoadingSkeletonContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  display: ${(props: LoadingSkeletonContainerProps) => (props.showSkeleton ? `block` : `none`)};
  width: 280px;
  height: 216px;
`,
);

type TopContainerProps = {
	showNavigation?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TopContainer = styled.div<TopContainerProps>({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: (props: TopContainerProps) =>
		props.showNavigation ? `${token('space.400')}` : `${token('space.0')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CloseButtonContainer = styled.div({
	position: 'absolute',
	right: `${token('space.150')}`,
	zIndex: 1,
	top: `${token('space.150')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ViewAllCommentsContainer = styled.div({
	position: 'absolute',
	top: `${token('space.200')}`,
	left: `${token('space.200')}`,
	display: 'flex',
	gap: `${token('space.075')}`,
	alignItems: 'center',
	justifyContent: 'center',
});

const closeButtonTheme =
	(isUpdatedStandaloneComment?: boolean) => (currentTheme: any, themeProps: any) => {
		const theme = currentTheme(themeProps);

		return {
			...theme,
			buttonStyles: {
				...theme.buttonStyles,
				height: HEADER_BUTTON_HEIGHT,
				width: isUpdatedStandaloneComment ? HEADER_BUTTON_WIDTH_STANDALONE : HEADER_BUTTON_WIDTH,
			},
		};
	};

export const CloseButton = ({ isUpdatedStandaloneComment, ...props }: InlineCloseButtonProps) => (
	<CustomThemeButton theme={closeButtonTheme(isUpdatedStandaloneComment ?? false)} {...props} />
);
